import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight, PawPrint, GraduationCap, Stethoscope, UtensilsCrossed, Sparkles, Leaf, LifeBuoy,
  ShieldCheck, Quote, Users, HandHeart, ChevronRight, Flower2, Baby, Palette, HeartHandshake,
  AlertTriangle, Building2, Star, CheckCircle2, PhoneCall, Heart, Clock, Award, Shield, FileText, Search,
} from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";
import {
  initiatives, foundationStats, values, pressMentions, leaders, supportCategories,
  foundationVision, daanAmrutLogo, IMG
} from "@/project/data/sample";

const logoUrl = daanAmrutLogo;

const ICONS: Record<string, typeof PawPrint> = {
  PawPrint, GraduationCap, Stethoscope, UtensilsCrossed, Sparkles, Leaf, LifeBuoy,
  Users, Flower2, Baby, Palette, HeartHandshake,
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Daan Amrut Foundation — Give with Heart. Give with Confidence." },
      { name: "description", content: "Daan Amrut Foundation brings together associations, communities, sanghs, clubs, animal shelters and non-profits with people who want to help. 80G & 12A certified." },
      { property: "og:title", content: "Daan Amrut Foundation" },
      { property: "og:description", content: "Give with Heart. Give with Confidence. India's transparent giving platform — 80G & 12A certified." },
      { property: "og:type", content: "website" },
    ],
  }),
  component: Home,
});

function Home() {
  return (
    <SiteShell>
      <HeroSection />
      <TrustBar />
      <CategoriesSection />
      <InitiativesSection />
      <FeaturedPanjrapoleSection />
      <TrustGridSection />
      <VisionSection />
      <LeadershipSection />
      <EmergencyRescueBanner />
      <GetInvolvedSection />
    </SiteShell>
  );
}

{/* ── 1. Hero Section (Large Split Banner - Dark Navy Theme matching reference) ───────── */}
function HeroSection() {
  return (
    <section className="relative bg-[#0c1226] text-white overflow-hidden py-14 sm:py-20 lg:py-24">
      {/* Background Subtle Gradient Lights */}
      <div className="absolute top-0 right-0 -z-10 h-[500px] w-[500px] rounded-full bg-[var(--saffron-deep)]/10 blur-[120px]" />
      <div className="absolute bottom-0 left-0 -z-10 h-[400px] w-[400px] rounded-full bg-emerald-600/10 blur-[100px]" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-8 items-center">
          
          {/* Left Text Content */}
          <div className="lg:col-span-7 flex flex-col">
            {/* Top Brand Tag */}
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md px-3.5 py-1.5 text-xs font-bold text-[var(--saffron)] border border-white/15 w-fit mb-6 shadow-sm">
              <img src={logoUrl} alt="" className="h-5 w-auto object-contain brightness-200" />
              <span className="uppercase tracking-wider text-[11px]">DAAN AMRUT FOUNDATION</span>
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--saffron)]" />
              <span className="text-white/80 font-normal">80G Certified</span>
            </div>

            <h1 className="font-display text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-[56px] text-white">
              Your gift becomes{" "}
              <br className="hidden sm:block" />
              someone's{" "}
              <span className="text-[var(--saffron)] underline decoration-[var(--saffron)]/40 underline-offset-8">
                new beginning.
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-base text-slate-300 leading-relaxed sm:text-lg">
              It's more than a donation — it's hope, love and a future transformed. Daan Amrut Foundation
              brings together associations, communities, sanghs, clubs, animal shelters and non-profits
              with people who want to help. Every rupee you give touches a real life.
            </p>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/donate"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--saffron)] px-8 py-3.5 text-sm font-extrabold text-[#0c1226] shadow-lg hover:bg-[var(--saffron-deep)] hover:text-white transition-all transform hover:-translate-y-0.5 active:scale-95 cursor-pointer"
              >
                <HandHeart className="h-4.5 w-4.5" />
                Donate Now
              </Link>
              <Link
                to="/register-institution"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/5 backdrop-blur-xs px-6 py-3.5 text-sm font-bold text-white hover:bg-white/15 transition-all cursor-pointer"
              >
                <Building2 className="h-4.5 w-4.5 text-[var(--saffron)]" />
                Register Gaushala
              </Link>
            </div>

            {/* Trust Social Proof Strip */}
            <div className="mt-10 pt-6 border-t border-white/10 flex items-center gap-4">
              <div className="flex items-center gap-1 text-[var(--saffron)]">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-current" />
                ))}
              </div>
              <p className="text-xs text-slate-300">
                <span className="font-bold text-white">1,850+</span> active volunteers & donors across Gujarat & India
              </p>
            </div>
          </div>

          {/* Right Collaged Impact Photos matching Figma screenshot */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-12 gap-3.5 items-stretch relative">
              {/* Left Large Vertical Photo Card */}
              <div className="col-span-7 overflow-hidden rounded-3xl border border-white/10 shadow-2xl relative min-h-[380px] group">
                <img
                  src={IMG.heroCow}
                  alt="Animal Seva"
                  className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c1226]/80 via-transparent to-transparent p-4 flex flex-col justify-end">
                  <span className="rounded-full bg-[var(--saffron)] px-2.5 py-0.5 text-[10px] font-extrabold text-[#0c1226] w-fit">GAUSHALA SEVA</span>
                  <p className="text-xs font-bold text-white mt-1">Gir Cow Fodder Drive</p>
                </div>
              </div>

              {/* Right Stacked Landscape Cards */}
              <div className="col-span-5 flex flex-col gap-3.5 justify-between">
                <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl relative group h-44">
                  <img
                    src={IMG.eventKitchen}
                    alt="Anna Daan"
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c1226]/80 to-transparent p-3 flex flex-col justify-end">
                    <p className="text-[11px] font-bold text-white leading-tight">Sadharmik & Anna Daan</p>
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl relative group h-44">
                  <img
                    src={IMG.eventMedical}
                    alt="Avian ICU"
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c1226]/80 to-transparent p-3 flex flex-col justify-end">
                    <p className="text-[11px] font-bold text-white leading-tight">Avian ICU Bird Care</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

{/* ── 2. Dark Navy Trust Bar ───────────────────────────────────────────── */}
function TrustBar() {
  const badges = [
    { label: "80G & 12A Certified", sub: "Tax-exempt receipts", Icon: ShieldCheck },
    { label: "Indian Citizens & Residents", sub: "Donations accepted", Icon: Users },
    { label: "Instant Digital Receipt", sub: "80G receipt by email", Icon: FileText },
    { label: "Verified Every Applicant", sub: "Multi-level audit", Icon: Shield },
    { label: "Trackable Fund Usage", sub: "Real-time updates", Icon: Clock },
    { label: "Annual Reports", sub: "Full transparency", Icon: Award },
  ];
  return (
    <section className="bg-[#080d1e] border-y border-white/10 text-white py-4">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {badges.map((b) => (
            <div key={b.label} className="flex items-center gap-3 p-2">
              <b.Icon className="h-5 w-5 text-[var(--saffron)] shrink-0" />
              <div>
                <p className="font-bold text-xs text-white leading-tight">{b.label}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{b.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const HOME_CATEGORIES = [
  { label: "Animal Seva", icon: "PawPrint" },
  { label: "Bird Care", icon: "Flower2" },
  { label: "Emergency Medical", icon: "Stethoscope" },
  { label: "Fodder Drive", icon: "UtensilsCrossed" },
  { label: "Anna Daan", icon: "HeartHandshake" },
  { label: "Education & Kits", icon: "GraduationCap" },
  { label: "Tree Plantation", icon: "Leaf" },
  { label: "Senior Care", icon: "Users" },
];

{/* ── 3. Warm Cream Section: Cause Categories Tile Grid ────────────────── */}
function CategoriesSection() {
  return (
    <section className="bg-[#fefaf3] py-16 sm:py-20 border-b border-amber-900/10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">WHAT YOU CAN SUPPORT</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold text-[var(--maroon-deep)] sm:text-4xl">
              Choose a cause that speaks to you
            </h2>
          </div>
          
          {/* Real Search Bar on the Right matching Figma reference */}
          <div className="relative flex items-center gap-2 rounded-full border border-amber-900/10 bg-white px-4 py-2.5 text-xs w-full sm:w-80 shadow-xs">
            <Search className="h-4 w-4 text-[var(--saffron-deep)] shrink-0" />
            <input
              type="text"
              placeholder="Search causes, donors, institutions..."
              className="bg-transparent outline-none w-full placeholder:text-muted-foreground text-xs"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-4">
          {HOME_CATEGORIES.map((c) => {
            const Icon = ICONS[c.icon] ?? PawPrint;
            return (
              <Link
                key={c.label}
                to="/causes"
                className="group flex items-center justify-between rounded-2xl border border-amber-900/10 bg-white p-4 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--cream-2)] text-[var(--maroon)] group-hover:bg-[var(--maroon)] group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-display text-xs font-extrabold text-[var(--maroon-deep)]">{c.label}</span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-[var(--maroon)] group-hover:translate-x-0.5 transition-all" />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

{/* ── 4. White Section: Our Initiatives Grid ───────────────────────────── */}
function InitiativesSection() {
  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">OUR INITIATIVES</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold text-[var(--maroon-deep)] sm:text-4xl">
              One foundation. Many fronts of seva.
            </h2>
          </div>
          <Link to="/initiatives" className="hidden sm:inline-flex shrink-0 items-center gap-1 text-sm font-bold text-[var(--maroon)] hover:gap-2 transition-all">
            View all <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Row 1: 3 Large Vertical Cards matching Figma screenshot */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {initiatives.slice(0, 3).map((i) => {
            const Icon = ICONS[i.icon] ?? PawPrint;
            return (
              <Link
                key={i.slug}
                to="/initiatives/$slug"
                params={{ slug: i.slug }}
                className="group relative overflow-hidden rounded-3xl border border-border/80 bg-white shadow-xs transition hover:-translate-y-1.5 hover:shadow-xl flex flex-col justify-between min-h-[380px]"
              >
                <div className="absolute inset-0 z-0">
                  <img
                    src={i.cover}
                    alt={i.name}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div
                    className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-black/10"
                  />
                </div>

                <div className="relative z-10 p-6 flex flex-col justify-between h-full text-white">
                  <div className="flex items-start justify-between">
                    <div className="grid h-11 w-11 place-items-center rounded-xl bg-white/10 backdrop-blur-md text-[var(--saffron)] border border-white/20 shadow-md">
                      <Icon className="h-5.5 w-5.5" />
                    </div>
                    {!i.live && (
                      <span className="rounded-full bg-[var(--saffron)]/90 px-3 py-1 text-[10px] font-extrabold text-[#0c1226] uppercase">
                        Coming Soon
                      </span>
                    )}
                  </div>

                  <div className="mt-20">
                    <p className="font-display text-2xl font-extrabold tracking-tight">{i.name}</p>
                    <p className="text-[11px] text-[var(--saffron)] font-bold uppercase tracking-wider mt-1">{i.tagline}</p>
                    <p className="text-xs text-slate-200 mt-2.5 leading-relaxed line-clamp-3 font-normal">{i.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[var(--saffron)] group-hover:gap-2 transition-all">
                      Explore Initiative <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Row 2: 4 Smaller Landscape Cards matching Figma screenshot */}
        <div className="grid gap-4 mt-6 grid-cols-2 lg:grid-cols-4">
          {initiatives.slice(3).map((i) => {
            const Icon = ICONS[i.icon] ?? PawPrint;
            return (
              <Link
                key={i.slug}
                to="/initiatives/$slug"
                params={{ slug: i.slug }}
                className="group relative overflow-hidden rounded-2xl border border-border/80 bg-white shadow-xs transition hover:-translate-y-1 hover:shadow-lg h-36 flex flex-col justify-end"
              >
                <div className="absolute inset-0 z-0">
                  <img
                    src={i.cover}
                    alt={i.name}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                </div>

                <div className="relative z-10 p-4 text-white">
                  <div className="flex items-center gap-2">
                    <div className="grid h-7 w-7 place-items-center rounded-lg bg-white/20 text-[var(--saffron)]">
                      <Icon className="h-4 w-4" />
                    </div>
                    <p className="font-display text-sm font-extrabold tracking-tight">{i.name}</p>
                  </div>
                  <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-[var(--saffron)] group-hover:gap-2 transition-all">
                    Explore <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

{/* ── 5. Large Dark Banner: Flagship Panjrapole & Gaushala Platform ─────── */}
function FeaturedPanjrapoleSection() {
  const seva = initiatives[0];
  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="overflow-hidden rounded-3xl bg-[#0c1226] text-white shadow-2xl border border-white/10 grid lg:grid-cols-12">
        
        {/* Left Column Image */}
        <div className="lg:col-span-5 relative min-h-[320px] lg:min-h-[480px]">
          <img
            src={IMG.causeFodder}
            alt="Panjrapole Cattle Seva"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-black/30 to-[#0c1226] lg:block hidden" />
          
          {/* Floating Stat Box */}
          <div className="absolute bottom-6 left-6 right-6 lg:right-auto rounded-2xl bg-white/15 backdrop-blur-md p-4 border border-white/20">
            <div className="flex items-center gap-4 text-white">
              <div>
                <p className="text-xl font-extrabold text-[var(--saffron)]">14+ Panjrapoles</p>
                <p className="text-xs text-slate-200">5,000+ Animals Sheltered 24x7</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column Details */}
        <div className="lg:col-span-7 p-8 sm:p-12 flex flex-col justify-center space-y-6">
          <div>
            <span className="rounded-full bg-[var(--saffron)]/20 border border-[var(--saffron)]/40 px-3.5 py-1 text-[11px] font-extrabold uppercase tracking-wider text-[var(--saffron)]">
              FLAGSHIP INITIATIVE · ANIMAL SEVA
            </span>
            <h2 className="mt-4 font-display text-2xl font-extrabold sm:text-3xl lg:text-4xl leading-tight text-white">
              Daan Amrut — a full donation platform for Panjrapoles & Gaushalas
            </h2>
            <p className="mt-3 text-slate-300 text-sm leading-relaxed">
              Discover verified Panjrapoles, Gaushalas, bird hospitals and rescue centres, pick a specific
              cause and see exactly how your money helped — with photos, bills and impact updates.
            </p>
          </div>

          <div className="space-y-2.5 text-xs text-slate-200">
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="h-4 w-4 text-[var(--saffron)] shrink-0" />
              <span><strong>100% Tax Deductible under 80G</strong> with instant email receipt</span>
            </div>
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="h-4 w-4 text-[var(--saffron)] shrink-0" />
              <span><strong>Direct Settlement</strong> to verified Gaushala bank accounts</span>
            </div>
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="h-4 w-4 text-[var(--saffron)] shrink-0" />
              <span><strong>Photo & Bill Proof</strong> for every fodder truckload & medical camp</span>
            </div>
          </div>

          <div className="pt-2 flex flex-wrap gap-3">
            <Link
              to="/initiatives/animal-seva"
              className="rounded-full bg-[var(--saffron)] px-7 py-3 text-xs font-extrabold text-[#0c1226] hover:bg-[var(--saffron-deep)] hover:text-white transition-all shadow-md cursor-pointer"
            >
              Donate Fodder Now →
            </Link>
            <Link
              to="/institutions"
              className="rounded-full border border-white/30 px-6 py-3 text-xs font-bold text-white hover:bg-white/10 transition"
            >
              Browse Institutions
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
}

{/* ── 6. Light Gray/Blue Section: 6 Numbered Trust Pillars ───────────── */}
function TrustGridSection() {
  const trustPillars = [
    { num: "01", title: "Fully Legal & Transparent", desc: "We accept donations only from compliant Indian accounts — ensuring every contribution is legal, safe, and accountable." },
    { num: "02", title: "Instant 80G Receipts", desc: "As soon as you donate, your 80G tax-exempt receipt is delivered instantly to your inbox and phone — no delays." },
    { num: "03", title: "Choose the Cause You Believe In", desc: "Support real students, families, gaushalas, and bird ICUs. Follow their journeys and see how your gift changes lives." },
    { num: "04", title: "Track Every Rupee", desc: "Get real-time impact updates, transparent fund usage, and access to annual audit reports — full visibility, always." },
    { num: "05", title: "Fraud-Proof Giving", desc: "Every applicant, gaushala, and fund is multi-verified by foundation auditors before money moves." },
    { num: "06", title: "Zero Administrative Cut", desc: "100% of your cause contribution reaches the ground — zero hidden fees or administrative middleman cuts." },
  ];

  return (
    <section className="bg-[#f4f7fa] py-16 sm:py-20 border-y border-slate-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-12 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">WHY DAAN AMRUT</p>
          <h2 className="mt-2 font-display text-3xl font-extrabold text-[var(--maroon-deep)] sm:text-4xl">
            Why Thousands Trust Daan Amrut
          </h2>
          <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">India's transparent, legal, and fraud-proof giving platform.</p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {trustPillars.map((v) => (
            <div key={v.num} className="rounded-2xl bg-white p-6 shadow-xs border border-slate-200/80 hover:shadow-md transition-shadow">
              <span className="font-display text-2xl font-extrabold text-[var(--saffron-deep)] block mb-3">{v.num}</span>
              <h3 className="font-display text-base font-bold text-[var(--maroon-deep)]">{v.title}</h3>
              <p className="mt-2 text-xs text-foreground/75 leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

{/* ── 7. Warm Cream Section: Vision & Values ────────────────────────────── */}
function VisionSection() {
  return (
    <section className="bg-[#fefaf3] py-16 sm:py-20 border-b border-amber-900/10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-8 rounded-3xl border border-amber-900/10 bg-white p-8 shadow-xs md:grid-cols-[1fr_1.2fr] md:p-12 items-center">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">OUR VISION</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold text-[var(--maroon-deep)] sm:text-4xl">
              A world where giving is easy.
            </h2>
            <p className="mt-4 text-foreground/75 text-sm leading-relaxed">{foundationVision.statement}</p>
            <Link
              to="/about"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[var(--maroon)] px-6 py-2.5 text-xs font-bold text-white shadow-xs hover:bg-[var(--maroon-deep)] transition"
            >
              Our full mission <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid gap-3.5 sm:grid-cols-2">
            {pressMentions.map((p) => (
              <div key={p.quote} className="rounded-2xl bg-[#fefaf3] p-5 border border-amber-900/10 shadow-2xs">
                <Quote className="h-4 w-4 text-[var(--saffron-deep)]" />
                <p className="mt-2 text-xs font-semibold leading-relaxed text-[var(--maroon-deep)]">"{p.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

{/* ── 8. White Section: Board Members & Advisory Council ───────────────── */}
function LeadershipSection() {
  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-10 flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">LEADERSHIP</p>
            <h2 className="mt-2 font-display text-3xl font-extrabold text-[var(--maroon-deep)] sm:text-4xl">
              Board Members & Advisory Council
            </h2>
          </div>
          <Link to="/leadership" className="hidden sm:inline-flex shrink-0 items-center gap-1 text-sm font-bold text-[var(--maroon)] hover:gap-2 transition-all">
            Full council <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {leaders.map((l) => (
            <div key={l.name} className="rounded-2xl bg-white p-6 shadow-xs border border-slate-200 flex flex-col justify-between hover:shadow-md transition">
              <div>
                <div className="flex items-center gap-4">
                  {l.photo ? (
                    <img
                      src={l.photo}
                      alt={l.name}
                      loading="lazy"
                      className="h-16 w-16 shrink-0 rounded-full object-cover object-[50%_15%] ring-2 ring-[var(--saffron)]/40 shadow-xs"
                    />
                  ) : (
                    <div className="grid h-16 w-16 shrink-0 place-items-center rounded-full bg-[var(--maroon)] font-display text-lg font-bold text-white">
                      {l.initials}
                    </div>
                  )}
                  <div>
                    <p className="font-display text-base font-bold text-[var(--maroon-deep)]">{l.name}</p>
                    <span className="inline-block mt-0.5 rounded-full bg-[var(--saffron)]/15 px-2.5 py-0.5 text-[10px] font-extrabold text-[var(--maroon-deep)] uppercase tracking-wider">
                      {l.role}
                    </span>
                  </div>
                </div>
                <p className="mt-4 text-xs text-foreground/75 leading-relaxed line-clamp-4">{l.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

{/* ── 9. Red / Dark Split Emergency Rescue SOS Banner ───────────────────── */}
function EmergencyRescueBanner() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="overflow-hidden rounded-3xl shadow-xl border border-red-200 grid lg:grid-cols-12">
        {/* Left Column Red Helpline */}
        <div className="lg:col-span-7 bg-gradient-to-r from-red-800 to-rose-900 text-white p-8 sm:p-10 flex flex-col justify-between">
          <div>
            <span className="rounded-full bg-white/15 px-3.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-white border border-white/20">
              24x7 EMERGENCY SOS HELPLINE
            </span>
            <h2 className="mt-4 font-display text-2xl font-extrabold sm:text-3xl text-white leading-tight">
              Report Injured Animals & Dispatch Rescue
            </h2>
            <p className="mt-2 text-xs text-rose-100/90 leading-relaxed max-w-lg">
              Report injured cattle, road accident animals, or downed birds. Animal ambulance dispatched in under 20 minutes.
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href="tel:+919876543210"
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-xs font-extrabold text-red-900 hover:bg-rose-50 transition shadow-md cursor-pointer"
            >
              <PhoneCall className="h-4 w-4 text-red-600" />
              Call Emergency Helpline
            </a>
            <Link
              to="/emergency"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/30 px-5 py-3 text-xs font-bold text-white hover:bg-white/10 transition"
            >
              Dispatch SOS <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        {/* Right Column Navy Quick Checklist */}
        <div className="lg:col-span-5 bg-[#0e162e] text-white p-8 sm:p-10 flex flex-col justify-center space-y-3">
          <p className="text-xs font-extrabold uppercase tracking-wider text-[var(--saffron)]">EMERGENCY RESPONSE PROTOCOL</p>
          <div className="space-y-2 text-xs text-slate-200">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <span><strong>20-Minute Dispatch</strong> for Sabarmati & Ahmedabad region</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <span><strong>Post-Op ICU Recovery Ward</strong> at Jivdaya Trust</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <span><strong>On-Call Veterinary Surgeon</strong> available 24x7</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

{/* ── 10. Light Section: Get Involved Cards ────────────────────────────── */}
function GetInvolvedSection() {
  const cta = [
    {
      img: IMG.eventMedical,
      title: "Volunteer",
      body: "Join our active volunteer network on the ground for feeding drives and emergency rescue.",
      link: "/volunteer",
      cta: "Join ground rescue →"
    },
    {
      img: IMG.eventTree,
      title: "Corporate CSR",
      body: "Partner with Daan Amrut for impactful corporate philanthropy and 80G tax benefits.",
      link: "/contact",
      cta: "Partner with us →"
    },
    {
      img: IMG.eventKitchen,
      title: "Partner NGO / Gaushala",
      body: "Register your animal shelter, panjrapole or trust to receive transparent donor funding.",
      link: "/register-institution",
      cta: "List your institution →"
    },
  ];

  return (
    <section className="bg-slate-50 py-16 sm:py-20 border-t border-slate-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-10 text-center">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">MULTIPLE WAYS TO HELP</p>
          <h2 className="mt-2 font-display text-3xl font-extrabold text-[var(--maroon-deep)] sm:text-4xl">Get involved today</h2>
          <p className="mt-1.5 text-xs text-muted-foreground">Every contribution matters. Pick your path.</p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {cta.map((c) => (
            <Link
              key={c.title}
              to={c.link}
              className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xs hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col justify-between"
            >
              <div>
                <div className="h-44 overflow-hidden relative">
                  <img src={c.img} alt={c.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                </div>
                <div className="p-6">
                  <h3 className="font-display text-lg font-bold text-[var(--maroon-deep)]">{c.title}</h3>
                  <p className="mt-2 text-xs text-foreground/75 leading-relaxed">{c.body}</p>
                </div>
              </div>
              <div className="px-6 pb-6">
                <span className="inline-flex items-center gap-1 text-xs font-extrabold text-[var(--maroon)] group-hover:gap-2 transition-all">
                  {c.cta}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
